// Which trips the calendar draws.
//
// Two surfaces over the same selection, because they answer different
// questions. The LEGEND is always on screen and says "this color is that trip"
// -- and clicking a chip takes a trip back off, which is the correction you
// most often want. The PANEL is an end-side Offcanvas (the idiom
// LayerTogglePanel established) holding every trip the item has ever had,
// which would swamp the page if it were always visible.
//
// The panel lists ALL trips, not just the upcoming ones. A trip is checked by
// default when it is still running (groupRunsOnOrAfter, not
// isUpcomingTripGroup -- see the comment on that function), but last summer's
// cruise is one click away rather than unreachable.

import Offcanvas from 'react-bootstrap/Offcanvas';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { tripGroupDateRange, tripGroupSummary } from '../../fleet-shared/TripGroups.mjs';
import { calendarColorVar } from './tripCalendarColors.mjs';

// A trip name may repeat across years, so the checkbox id is built from the
// group key -- unique by construction -- with everything a DOM id can't hold
// replaced.
const checkboxId = (key) => `trip-pick-${String(key).replace(/[^a-zA-Z0-9_-]/g, '-')}`;

export const TripLegend = ({ groups, colorIndexFor, onRemove }) => {
  if (!groups.length) return null;
  return (
    <div className="trip-cal-legend">
      {groups.map((group) => (
        <button
          key={group.key}
          type="button"
          className="trip-cal-legend__chip"
          style={{ '--trip-color': calendarColorVar(colorIndexFor(group.key)) }}
          onClick={() => onRemove(group.key)}
          aria-label={`Hide ${group.name} from the calendar`}
        >
          <span className="trip-cal-swatch" aria-hidden="true" />
          <span className="trip-cal-legend__name">{group.name}</span>
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
};

const TripPicker = ({
  show,
  onHide,
  groups,
  selectedKeys,
  colorIndexFor,
  placeIndex,
  onToggle,
  onSelectAll,
  onSelectNone,
  onSelectRunning,
}) => (
  <Offcanvas show={show} onHide={onHide} placement="end">
    <Offcanvas.Header closeButton>
      <Offcanvas.Title>Show trips</Offcanvas.Title>
    </Offcanvas.Header>
    <Offcanvas.Body>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button size="sm" variant="outline-secondary" onClick={onSelectRunning}>Upcoming</Button>
        <Button size="sm" variant="outline-secondary" onClick={onSelectAll}>All</Button>
        <Button size="sm" variant="outline-secondary" onClick={onSelectNone}>None</Button>
      </div>

      {groups.length === 0 ? (
        <p className="text-body-secondary">No trips to show yet.</p>
      ) : (
        <Form>
          {groups.map((group) => (
            <div key={group.key} className="mb-3">
              <div className="trip-cal-picker__row">
                <span
                  className="trip-cal-swatch"
                  aria-hidden="true"
                  style={{ '--trip-color': calendarColorVar(colorIndexFor(group.key)) }}
                />
                <Form.Check
                  type="checkbox"
                  id={checkboxId(group.key)}
                  className="mb-0"
                  label={group.name}
                  checked={selectedKeys.has(group.key)}
                  onChange={(event) => onToggle(group.key, event.target.checked)}
                />
              </div>
              {/* Said under the name rather than in the label, so the accessible
                  name of the checkbox stays the trip's name. */}
              <div className="trip-cal-picker__meta ms-4 ps-2">
                {[tripGroupDateRange(group), tripGroupSummary(group, { placeIndex })]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          ))}
        </Form>
      )}
    </Offcanvas.Body>
  </Offcanvas>
);

export default TripPicker;
